import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { AppletAuditTrailContainer, AppletAuditTrailTenantEpService, Pagination } from 'blg-akaun-ts-lib';
import { pageFiltering, pageSorting } from '../../../../../listing.utils';
import { PaginationComponent } from '../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { Observable } from 'rxjs';
import { SubSink } from 'subsink2';
import moment from 'moment';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { AuditTrailActions } from '../../../settings-controller/applet-audit-trail/actions';
import { AuditTrailSelectors } from '../../../settings-controller/applet-audit-trail/selectors';
import { AuditTrailState } from '../../../settings-controller/applet-audit-trail/states/applet-audit-trail.states';
import { AppletAuditTrail } from '../../../../../models/applet-audit-trail.model';

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'applet-audit-trail-listing',
  templateUrl: './applet-audit-trail-listing.component.html',
  styleUrls: ['./applet-audit-trail-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class AppletAuditTrailListingComponent extends ViewColumnComponent {

  private subs = new SubSink();

  protected readonly index = 0;
  private localState: LocalState;

  baseConditionalCriteria = [
    { columnName: "calcTotalRecords", operator: "=", value: "true" },
    { columnName: 'orderBy', operator: '=', value: 'date_updated' },
    { columnName: 'order', operator: '=', value: 'DESC' },
  ];
  defaultConditionalCriteria = [...this.baseConditionalCriteria]

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.deactivateAdd);

  toggleColumn$: Observable<boolean>;
  appletGuid = sessionStorage.getItem('appletGuid');
  gridApi;
  rowData = [];
  SQLGuids: string[] = null;
  pagination = new Pagination();

  selectedRowIndex: number;
  searchModel = AppletAuditTrail;

  defaultColDef = {
    filter: 'agTextColumnFilter',
    floatingFilterComponentParams: { suppressFilterButton: true },
    minWidth: 200,
    flex: 2,
    sortable: true,
    resizable: true,
    suppressCsvExport: true,
  };

  columnsDefs = [
    { headerName: 'Table Name', field: 'bl_applet_audit_trail.foreign_table', cellStyle: () => ({ 'text-align': 'left' }) },
    { headerName: 'Action', field: 'bl_applet_audit_trail.action', cellStyle: () => ({ 'text-align': 'left' }) },
    // { headerName: 'Action By', field: 'bl_aat_claim_cycle_audit_trail.action_by_subject_name', cellStyle: () => ({ 'text-align': 'left' }) },
    { headerName: 'Action Date', field: 'bl_applet_audit_trail.date_created', cellStyle: () => ({ 'text-align': 'left' }),
      valueFormatter: (params) => moment(params.value).format('YYYY-MM-DD')
    },
    { headerName: 'Description', field: 'bl_applet_audit_trail.descr', cellStyle: () => ({ 'text-align': 'left' }) },
    { headerName: 'Status', field: 'bl_applet_audit_trail.status', cellStyle: () => ({ 'text-align': 'left' }) },
  ];
  @ViewChild(PaginationComponent) paginationComp: PaginationComponent;
  constructor(
    private appletAuditTrailService : AppletAuditTrailTenantEpService ,
    private viewColFacade: PermissionFacade,
    private readonly store: Store<AuditTrailState>,
    private readonly componentStore: ComponentStore<LocalState>
  ) {
    super();
  }

  ngOnInit() {
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subs.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState(a);
      if (a.rowIndexListing === null) {
        this.gridApi?.deselectAll();
      }
    });
  }

  setGridData() {
    const apiVisa = AppConfig.apiVisa;
    const datasource = {
      getRows: (grid) => {
        this.store.dispatch(
          AuditTrailActions.loadAuditTrailInit({ request: grid.request })
        );
        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);
        this.pagination.offset = this.SQLGuids ? 0 : grid.request.startRow;
        this.pagination.limit = this.SQLGuids
        ? grid.request.endRow - grid.request.startRow
        : grid.request.endRow;
        let criteriaToAppend = [];
        criteriaToAppend.push(
          {
            columnName: 'guids',
            operator: '=',
            value: this.SQLGuids
              ? this.SQLGuids.slice(
                grid.request.startRow,
                grid.request.endRow
              ).toString()
              : '',
          },
        );
        criteriaToAppend.push(
          {
            columnName: 'applet_guid',
            operator: '=',
            value: this.appletGuid,
          },
        );
        this.baseConditionalCriteria = this.baseConditionalCriteria.concat(criteriaToAppend);
        this.pagination.conditionalCriteria = this.baseConditionalCriteria;
      
        this.subs.sink = this.appletAuditTrailService
          .getByCriteria(this.pagination, apiVisa)
          .subscribe(
            (a) => {
              grid.success({
                rowData: a.data,
                rowCount: a.totalRecords,
              });
             
              this.store.dispatch(
                AuditTrailActions.loadAuditTrailSuccess({totalRecords: a.totalRecords})
              );
              this.resetBaseConditionalCriteria();
            },
            (err) => {
              grid.fail();
              this.store.dispatch(AuditTrailActions.loadAuditTrailFailure({error: err.message })
              );
            }
          );
      },
    };
    this.gridApi.setServerSideDatasource(datasource);
    this.subs.sink = this.store
      .select(AuditTrailSelectors.selectAgGrid)
      .subscribe((resolved) => {
        if (resolved) {
          this.gridApi.refreshServerSideStore();
          this.store.dispatch(AuditTrailActions.updateAgGridDone({ done: false }));
        }
      });
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    this.setGridData();
  }

  onRowClicked(event) {
    this.selectedRowIndex = event.rowIndex;
    const auditTrail: AppletAuditTrailContainer = event.data;

    this.store.dispatch(AuditTrailActions.selectedAuditTrail({ auditTrail: auditTrail }));
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index, {
        ...this.localState,
        deactivateAdd: false,
        deactivateList: true,
        rowIndexListing: this.selectedRowIndex
      });
      this.viewColFacade.onNextAndReset(this.index, 1);
    }
  }

  onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  resetBaseConditionalCriteria() {
    this.baseConditionalCriteria = JSON.parse(JSON.stringify(this.defaultConditionalCriteria));
  }

  onSearch($event) {
    let criteriaToAppend = [];
    if ($event) {
      criteriaToAppend.push(
        { columnName: 'search_word', operator: '=', value: $event },
      );
    }

    this.baseConditionalCriteria = this.baseConditionalCriteria.concat(criteriaToAppend);
    this.setGridData();
  }

  onAdvancedSearch($event) {
    let criteriaToAppend = [];

    if ($event.tabelName) {
      criteriaToAppend.push(
        { columnName: 'table_name', operator: '=', value: $event.tabelName },
      );
    }
    if ($event.ActionName) {
      criteriaToAppend.push(
        { columnName: 'action', operator: '=', value: $event.ActionName },
      );
    }
    if ($event.actionBy) {
      criteriaToAppend.push(
        { columnName: 'action_by_subject_name', operator: '=', value: $event.actionBy },
      );
    }
    if ($event.actionDate) {
      if ($event.actionDate?.from) {
        criteriaToAppend.push(
          { columnName: 'action_date_from', operator: '=', value: $event.actionDate?.from?.toISOString() },
        );
      }
      if ($event.actionDate?.to) {
        criteriaToAppend.push(
          { columnName: 'action_date_to', operator: '=', value: $event.actionDate?.to?.toISOString() },
        );
      }
      this.baseConditionalCriteria = this.baseConditionalCriteria.concat(criteriaToAppend);
      this.setGridData();
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
